import { inject, observer } from 'mobx-react';
import { RouteComponentProps, useHistory, useLocation, withRouter } from 'react-router-dom';
import * as React from 'react';

import { Layer } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { QueryRoute, updateSearch } from 'bernie-core';

import { useClickTracker } from '@shared-ui/clickstream-analytics-context';

import { SearchResultsStore } from 'stores/search-results-store';

import { RfrrPageNamespace } from 'constants/analytics';
import { SearchFormFields } from 'models';

import { UitkBadge, UitkBadgeSize, UitkBadgeTheme } from 'uitk-react-badge';
import { UitkIcon } from 'uitk-react-icons';
import { UitkSpacing } from 'uitk-react-spacing';

import { FiltersDialog } from './filters-dialog';

export interface FiltersToggleProps extends RouteComponentProps {
  className?: string;
  searchResults?: SearchResultsStore;
  showActions?: boolean;
  isActive: boolean;
  buttonComponent: React.ReactNode;
  isFullWidth: boolean;
  label: string;
  a11yMessage: string;
  rfrr: string;
  stid: string;
  filters: SearchFormFields;
  dialogParam: string;
  dialogTitle: string;
}
export type LocalizedFiltersToggleProps = LocalizedComponentProps & FiltersToggleProps;

@withLocalization
@inject('searchResults')
@observer
class FiltersToggleComponent extends React.Component<LocalizedFiltersToggleProps> {
  private filtersBtn: React.RefObject<HTMLElement>;

  constructor(props: LocalizedFiltersToggleProps) {
    super(props);

    this.filtersBtn = React.createRef();
    this.onDismiss = this.onDismiss.bind(this);
    this.state = { mounted: false };
  }

  componentDidMount() {
    // This is a hack to fix https://jira.expedia.biz/browse/BHS-20095
    // It is likely a bug in bernie that was unnoticed because the re-render fixed by #4171 was hiding this bug
    setTimeout(() => this.setState({ mounted: true }), 0);
  }

  public render() {
    const {
      isActive = false,
      searchResults,
      buttonComponent,
      isFullWidth,
      label,
      a11yMessage,
      rfrr,
      stid,
      filters,
      dialogParam,
      dialogTitle,
      l10n,
    } = this.props;

    return (
      <React.Fragment>
        <FiltersButton
          a11yMessage={a11yMessage}
          buttonComponent={buttonComponent}
          dialogParam={dialogParam}
          filtersBtn={this.filtersBtn}
          isActive={isActive}
          isFullWidth={isFullWidth}
          l10n={l10n}
          label={label}
          rfrr={rfrr}
          searchResults={searchResults}
          stid={stid}
        />
        <QueryRoute query={{ pwaDialog: dialogParam }}>
          <Layer id="tools-filterdrawer" onDismiss={this.onDismiss}>
            <FiltersDialog
              searchCriteria={searchResults.searchCriteria}
              onDismiss={this.onDismiss}
              {...{ searchResults, filters, dialogTitle }}
            />
          </Layer>
        </QueryRoute>
      </React.Fragment>
    );
  }

  private onDismiss(): void {
    const focus = this.filtersBtn?.current?.focus;
    this.props.history.goBack();

    if (typeof focus === 'function') {
      this.filtersBtn.current.focus();
    }
  }
}

const FiltersButton = ({
  a11yMessage,
  buttonComponent,
  dialogParam,
  filtersBtn,
  isActive,
  isFullWidth,
  l10n,
  label,
  rfrr,
  searchResults,
  stid,
}) => {
  const { isLoading, isError, sortAndFilter } = searchResults;
  const appliedFilterCount = sortAndFilter?.options?.appliedFilterCount;
  const ButtonTracked = buttonComponent;
  const filtersApplied = appliedFilterCount > 0;
  const ariaLabel = `${filtersApplied ? `${l10n.formatText(a11yMessage, appliedFilterCount)}, ` : ''}${l10n.formatText(label)}`;
  const hasFilter = sortAndFilter && Object.keys(sortAndFilter).length > 0;
  const track = useClickTracker();
  const history = useHistory();
  const location = useLocation();
  const extraProps =
    isActive && hasFilter
      ? {
          role: 'button',
        }
      : {};
  const linkName = 'Launch filters takeover';
  const sendTracking = () => track(`${RfrrPageNamespace.Search}${rfrr}`, linkName);

  const onClick =
    isActive && hasFilter
      ? () => {
          updateSearch({ history, location, newParams: { pwaDialog: dialogParam } });
          sendTracking();
        }
      : sendTracking;

  return (
    <ButtonTracked
      innerRef={filtersBtn}
      data-stid={stid}
      disabled={!isActive || isLoading || isError || null}
      isFullWidth={isFullWidth}
      aria-label={ariaLabel}
      onClick={onClick}
      {...extraProps}
    >
      <UitkSpacing margin={{ inlineend: 'two' }}>
        {filtersApplied ? (
          <UitkBadge theme={UitkBadgeTheme.NOTIFICATION_1} text={appliedFilterCount} size={UitkBadgeSize.SMALL} />
        ) : (
          <UitkIcon name="tune" />
        )}
      </UitkSpacing>

      <LocalizedText message={label} />
    </ButtonTracked>
  );
};

export const FiltersToggle = withRouter(FiltersToggleComponent);
